declare global {
  interface Window {
    __ENV__?: {
      VITE_SERVICE_NAME?: string;
      VITE_SERVICE_VERSION?: string;
      MODE?: string;
      VITE_TELEMETRY_ENABLED?: string;
      VITE_TELEMETRY_COLLECTOR_URL?: string;
      VITE_PUBLIC_POSTHOG_KEY?: string;
      VITE_PUBLIC_POSTHOG_HOST?: string;
      VITE_API_URL?: string;
      VITE_API_WS_URL?: string;
      VITE_WORKOS_CLIENT_ID?: string;
      VITE_HERE_API_KEY?: string;
      VITE_PROTOMAPS_API_KEY?: string;
      VITE_WOPI_BASE_URL?: string;
      VITE_COLLABORA_BASE_URL?: string;
      VITE_PUBLIC_TALKJS_APP_ID?: string;
      VITE_EXECUTION_MODE?: string;
      VITE_SEMAPHOR_EMBED_URL?: string;
      E2E_EMAIL?: string;
      E2E_PASSWORD?: string;
    };
  }
}

/**
 * Get configuration value from runtime environment or build-time fallback
 *
 * In production: reads from window.__ENV__ (injected at deployment)
 * In development: falls back to import.meta.env (Vite build-time values)
 */
function getEnv(key: keyof NonNullable<Window['__ENV__']>): string | undefined {
  // Try runtime config first (production)
  if (typeof window !== 'undefined' && window.__ENV__?.[key]) {
    return window.__ENV__[key];
  }

  // Fall back to build-time config (development)
  // eslint-disable-next-line custom/no-direct-env-access
  return import.meta.env[key];
}

export const runtimeConfig = {
  service: {
    get name() {
      return getEnv('VITE_SERVICE_NAME') || 'frontend-web-console';
    },
    get version() {
      return getEnv('VITE_SERVICE_VERSION') || '0.0.0';
    },
  },

  get environment() {
    return getEnv('VITE_EXECUTION_MODE') || 'development';
  },

  openTelemetry: {
    get enabled() {
      return getEnv('VITE_TELEMETRY_ENABLED') === 'true';
    },
    get collector() {
      return getEnv('VITE_TELEMETRY_COLLECTOR_URL');
    },
  },

  posthog: {
    get enabled() {
      return Boolean(getEnv('VITE_PUBLIC_POSTHOG_KEY'));
    },
    get key() {
      return getEnv('VITE_PUBLIC_POSTHOG_KEY');
    },
    get host() {
      return getEnv('VITE_PUBLIC_POSTHOG_HOST') || 'https://us.i.posthog.com';
    },
  },

  api: {
    get url() {
      return getEnv('VITE_API_URL') ?? 'https://api.k8s.orb.local';
    },
  },

  websocket: {
    get url() {
      return getEnv('VITE_API_WS_URL') ?? 'wss://api.k8s.orb.local';
    },
  },

  workOs: {
    get clientId() {
      return getEnv('VITE_WORKOS_CLIENT_ID');
    },
  },

  here: {
    get apiKey() {
      return getEnv('VITE_HERE_API_KEY');
    },
  },

  protomaps: {
    get apiKey() {
      return getEnv('VITE_PROTOMAPS_API_KEY');
    },
  },

  e2e: {
    get email() {
      return getEnv('E2E_EMAIL');
    },
    get password() {
      return getEnv('E2E_PASSWORD');
    },
  },

  office: {
    wopi: {
      get baseUrl() {
        return (
          getEnv('VITE_WOPI_BASE_URL') || 'https://api.k8s.orb.local/wopi/files'
        );
      },
    },
    collabora: {
      get baseUrl() {
        return (
          getEnv('VITE_COLLABORA_BASE_URL') || 'https://collabora.k8s.orb.local'
        );
      },
    },
  },

  talkJs: {
    get appId() {
      return getEnv('VITE_PUBLIC_TALKJS_APP_ID') || 'tEhjS4RO';
    },
  },

  semaphor: {
    get embedUrl() {
      return (
        getEnv('VITE_SEMAPHOR_EMBED_URL') || 'https://semaphor.cloud/embed'
      );
    },
  },
} as const;
